import { router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  IdCard,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { PersonForEntrance, SharedProps } from '@/types';
import { AssignCredentialDialog } from './AssignCredentialDialog';

interface EntranceTableProps {
  people: PersonForEntrance[];
  eventSlug: string;
}

export function EntranceTable({ people, eventSlug }: EntranceTableProps) {
  const [isPending, setIsPending] = useState(false);
  const [selectedPerson, setSelectedPerson] =
    useState<PersonForEntrance | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credentialPerson, setCredentialPerson] =
    useState<PersonForEntrance | null>(null);
  const [credentialMode, setCredentialMode] = useState<'assign' | 'entrance'>(
    'assign',
  );

  const markEntrance = (person: PersonForEntrance) => {
    if (person.entranceId || !person.onboarded) return;

    setIsPending(true);
    router.post(
      `/admin/${eventSlug}/entrance/mark`,
      { personId: person.id, personType: person.personType },
      {
        preserveScroll: true,
        onSuccess: (page) => {
          const { flash } = page.props as unknown as SharedProps;

          if (flash.alert) {
            toast({ type: 'error', description: flash.alert });
          } else {
            toast({
              type: 'success',
              description: flash.notice ?? 'Entrance registered',
            });
          }
        },
        onError: () =>
          toast({ type: 'error', description: 'Failed to register entrance' }),
        onFinish: () => {
          setIsPending(false);
          setDialogOpen(false);
          setSelectedPerson(null);
        },
      },
    );
  };

  const handleMarkEntrance = (person: PersonForEntrance) => {
    if (!person.credentialSlug) {
      setCredentialMode('entrance');
      setCredentialPerson(person);
      return;
    }

    setSelectedPerson(person);
    setDialogOpen(true);
  };

  const handleCredential = (person: PersonForEntrance) => {
    setCredentialMode('assign');
    setCredentialPerson(person);
  };

  const confirmEntrance = () => {
    if (!selectedPerson) return;
    markEntrance(selectedPerson);
  };

  const getGithubUsername = (github: string) => {
    return github
      .replace(/^https?:\/\/(www\.)?github\.com\//, '')
      .replace(/\/$/, '');
  };

  if (people.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>Search by name or email to register entrances.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Type</TableHead>
              <TableHead className="w-[250px]">Name</TableHead>
              <TableHead>GitHub</TableHead>
              <TableHead>Shoe Size</TableHead>
              <TableHead>Shirt Size</TableHead>
              <TableHead>Credential</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.map((person) => (
              <TableRow key={`${person.personType}-${person.id}`}>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      person.personType === 'hacker'
                        ? 'border-blue-300 bg-blue-100 text-blue-800'
                        : 'border-purple-300 bg-purple-100 text-purple-800'
                    }
                  >
                    {person.personType === 'hacker' ? 'Hacker' : 'Mentor'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{person.fullName}</span>
                    {person.email && (
                      <span className="text-muted-foreground text-sm">
                        {person.email}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {person.github ? (
                    <a
                      href={
                        person.github.startsWith('http')
                          ? person.github
                          : `https://github.com/${person.github}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      {getGithubUsername(person.github)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {person.shoeSize ? (
                    <Badge variant="outline">{person.shoeSize}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {person.shirtSize ? (
                    <Badge variant="outline">{person.shirtSize}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1"
                    onClick={() => handleCredential(person)}
                  >
                    <IdCard className="mr-1 h-4 w-4" />
                    {person.credentialSlug ? (
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {person.credentialSlug}
                      </code>
                    ) : (
                      <span className="text-muted-foreground text-xs">
                        Assign
                      </span>
                    )}
                  </Button>
                </TableCell>
                <TableCell>
                  {!person.onboarded ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button">
                          <Badge className="cursor-pointer border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            Sin onboarding
                          </Badge>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 text-sm">
                        {person.fullName} aún no completa su onboarding. Pídele
                        que lo complete y que revise su mail; no puede entrar
                        hasta entonces.
                      </PopoverContent>
                    </Popover>
                  ) : person.entranceId ? (
                    <Badge className="border-green-300 bg-green-100 text-green-800">
                      <Check className="mr-1 h-3 w-3" />
                      Entered
                      {person.enteredAt && (
                        <span className="ml-1 text-xs opacity-75">
                          {formatDistanceToNow(new Date(person.enteredAt), {
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      Not entered
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {person.entranceId ? (
                    <Button variant="outline" size="sm" disabled>
                      <Check className="mr-1 h-4 w-4" />
                      Entered
                    </Button>
                  ) : !person.onboarded ? (
                    <Button variant="outline" size="sm" disabled>
                      Mark Entrance
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleMarkEntrance(person)}
                      disabled={isPending}
                    >
                      {isPending && selectedPerson?.id === person.id ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : null}
                      Mark Entrance
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AssignCredentialDialog
        open={credentialPerson !== null}
        onOpenChange={(open) => {
          if (!open) setCredentialPerson(null);
        }}
        person={credentialPerson}
        eventSlug={eventSlug}
        mode={credentialMode}
        isEntering={isPending}
        onAssigned={(person) => {
          if (credentialMode === 'entrance') markEntrance(person);
          setCredentialPerson(null);
        }}
        onSkip={(person) => {
          markEntrance(person);
          setCredentialPerson(null);
        }}
      />

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Entrance</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark{' '}
              <span className="font-semibold">{selectedPerson?.fullName}</span>{' '}
              as entered? This will send a Discord notification.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmEntrance} disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirming...
                </>
              ) : (
                'Confirm Entrance'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
